import React, { useState } from 'react'
import { X, Download, AlertTriangle, Loader2 } from 'lucide-react'
import { PDFViewer, pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import { useProjectReportData } from '@/hooks/useProjectReportData'
import { ProjectReportDocument } from './ProjectReportDocument'
import { toast } from 'react-hot-toast'

interface ProjectReportModalProps {
  projectId: number
  onClose: () => void
}

/**
 * Full-screen modal hosting the live PDF preview + a download button.
 * The query is enabled the moment the modal mounts; closing unmounts.
 */
export function ProjectReportModal({
  projectId,
  onClose,
}: ProjectReportModalProps): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useProjectReportData(
    projectId,
    { enabled: true },
  )
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async (): Promise<void> => {
    if (!data) return
    try {
      setDownloading(true)
      const blob = await pdf(<ProjectReportDocument data={data} />).toBlob()
      const safeName = (data.project.name || `project_${projectId}`)
        .replace(/[^a-zA-Z0-9-_]/g, '_')
        .replace(/_+/g, '_')
      const today = new Date().toISOString().slice(0, 10)
      saveAs(blob, `${safeName}_Report_${today}.pdf`)
    } catch (e) {
      console.error('Failed to generate PDF blob', e)
      toast.error('Failed to download PDF — please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const projectName = data?.project?.name ?? 'Project Report'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Project report preview"
    >
      <div className="relative flex h-[90vh] w-[90vw] flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-secondary-200 bg-white px-5 py-3">
          <div className="min-w-0 flex-1 pr-4">
            <h2 className="truncate text-base font-semibold text-secondary-900">
              {projectName}
            </h2>
            <p className="text-xs text-secondary-500">Project Report — Preview</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!data || isLoading || downloading}
              className="btn-primary flex items-center disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-2 text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden bg-secondary-100">
          {isLoading && (
            <div className="flex h-full items-center justify-center">
              <div className="flex flex-col items-center text-secondary-600">
                <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary-600" />
                <p className="text-sm">Generating project report…</p>
              </div>
            </div>
          )}

          {isError && !isLoading && (
            <div className="flex h-full items-center justify-center">
              <div className="max-w-md text-center">
                <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-red-500" />
                <h3 className="mb-1 text-base font-semibold text-secondary-900">
                  Could not load report
                </h3>
                <p className="mb-4 text-sm text-secondary-600">
                  {(error as Error | undefined)?.message ||
                    'The server did not return the report payload. Please try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="btn-secondary"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {data && !isLoading && !isError && (
            <PDFViewer
              width="100%"
              height="100%"
              showToolbar={false}
              style={{ border: 'none' }}
            >
              <ProjectReportDocument data={data} />
            </PDFViewer>
          )}
        </div>
      </div>
    </div>
  )
}
